import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserDetails } from '../services/org-board-builder.service';
import { Component, Inject, inject, OnInit, OnDestroy } from '@angular/core';
import { staffPositionLines } from '../shared/part-location';
import { HatLocationPipe } from '../shared/hat-location.pipe';
import {
    Organization,
    OrgUser,
    PolicyAcknowledgment,
    OrgPolicy,
    OrgHat,
    PendingPositionApproval,
} from '@newgenus/common';
import { MaterialModule } from '../../material.module';
import { NormalizePipe } from '../format-pipe';
import { boardSeatPostKeys } from '../detached-group-rules';
import { CommonModule } from '@angular/common';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { OrgDialogService } from '../services/org-dialog.service';
import { OrgHattingService } from '../services/org-hatting.service';
import { ProfileColorService } from '../services/profile-color.service';
import firebase from 'firebase/compat/app';
import { Subject, skip, take, takeUntil, firstValueFrom, Subscription } from 'rxjs';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';

/**
 * User policy display item - includes both acknowledged and unacknowledged policies
 */
export interface UserPolicyItem {
    policyKey: string;
    policyName: string;
    policy?: OrgPolicy;
    acknowledgment?: PolicyAcknowledgment;
    receivedViaHats: string[]; // Hat names through which this policy was received
    status: 'acknowledged' | 'pending' | 'not-required';
    isCurrentlyAssigned: boolean; // Whether user is currently on a targeting hat
    isPending?: boolean; // Whether this policy comes from a pending (not yet approved) hat assignment
}

/**
 * Tree node for Permission Lineage visualization
 * Shows: User → Hats → Security Groups → Features → Permissions
 */
export interface LineageTreeNode {
    name: string;
    type: 'hat' | 'position' | 'security-group' | 'feature' | 'permission';
    icon: string;
    key?: string;
    children?: LineageTreeNode[];
    /** For positions: the parent hierarchy chain (e.g., "Division 2 → CEO") */
    hierarchy?: string;
}

/**
 * Tree node for Policy Lineage visualization
 * Shows: Policy → Source (Hat/Part/Direct) → Position hierarchy
 */
export interface PolicyLineageNode {
    name: string;
    type: 'policy' | 'source-hat' | 'source-part' | 'source-direct' | 'position' | 'status';
    icon: string;
    key?: string;
    children?: PolicyLineageNode[];
    /** For positions: the parent hierarchy chain */
    hierarchy?: string;
    /** Policy status info */
    status?: 'acknowledged' | 'pending' | 'requires-reack';
    /** Additional details */
    details?: string;
}

/**
 * Tree node for Position Lineage visualization
 * Shows: Position → Hats that place user here → hierarchy
 */
export interface PositionLineageNode {
    name: string;
    type: 'position' | 'hat';
    icon: string;
    key?: string;
    children?: PositionLineageNode[];
    /** For positions: the parent hierarchy chain */
    hierarchy?: string;
}

/**
 * Tree node for Security Group Lineage visualization
 * Shows: Security Group → Source (Hat/Direct) → Features → Permissions
 */
export interface SecurityGroupLineageNode {
    name: string;
    type: 'security-group' | 'source-hat' | 'source-direct' | 'feature' | 'permission' | 'position';
    icon: string;
    key?: string;
    children?: SecurityGroupLineageNode[];
    /** For positions: the parent hierarchy chain */
    hierarchy?: string;
}

/**
 * Tree node for Menu Lineage visualization
 * Shows: Menu Section → Menu Item → Feature → Source (Security Group/Direct) → Hat → Position
 */
export interface MenuLineageNode {
    name: string;
    type: 'section' | 'menu-item' | 'feature' | 'security-group' | 'hat' | 'position' | 'direct';
    icon: string;
    key?: string;
    children?: MenuLineageNode[];
    /** For positions: the parent hierarchy chain */
    hierarchy?: string;
}

// Organization features that trigger system route access (any of these grants access to default org pages)
const ORG_SYSTEM_FEATURES = [
    'organization',
    'organization-dashboard',
    'organization-settings',
    'organization-integrations',
    'organization-targets',
    'organization-dates',
];

// Management tools features that trigger system route access (any of these grants access to dashboard)
const MGMT_SYSTEM_FEATURES = [
    'management-tools',
    'management-tools-dashboard',
    'statistics',
    'checklist',
    'client-abc',
];

// Menu configuration for preview
const MENU_ITEMS: { feature: string; name: string; icon: string; section: string }[] = [
    // Home section (top-level navigation)
    { feature: 'workspace', name: 'WorkSpace', icon: 'account_balance_wallet', section: 'Home' },
    { feature: 'management-tools-dashboard', name: 'Management Tools', icon: 'engineering', section: 'Home' },
    { feature: 'organization', name: 'Organization', icon: 'corporate_fare', section: 'Home' },
    // Workspace section
    { feature: 'workspace', name: 'Inbasket', icon: 'inbox', section: 'Workspace' },
    { feature: 'workspace', name: 'Pending', icon: 'pending', section: 'Workspace' },
    { feature: 'workspace', name: 'Outbasket', icon: 'outbox', section: 'Workspace' },
    { feature: 'workspace', name: 'Filed', icon: 'task_alt', section: 'Workspace' },
    { feature: 'workspace', name: 'Search', icon: 'search', section: 'Workspace' },
    // Management Tools
    { feature: 'management-tools-dashboard', name: 'Dashboard', icon: 'dashboard', section: 'Management Tools' },
    { feature: 'checklist', name: 'Checklist', icon: 'checklist', section: 'Management Tools' },
    { feature: 'statistics', name: 'Statistics', icon: 'analytics', section: 'Management Tools' },
    { feature: 'client-abc', name: 'Client ABC', icon: 'hotel_class', section: 'Management Tools' },
    // Organization (system routes - accessible with any org feature via system route inheritance)
    { feature: 'organization', name: 'Dashboard', icon: 'dashboard', section: 'Organization' },
    { feature: 'organization', name: 'My Hats', icon: 'school', section: 'Organization' },
    { feature: 'organization', name: 'My Policies', icon: 'fact_check', section: 'Organization' },
    { feature: 'organization', name: 'Org Board', icon: 'account_tree', section: 'Organization' },
    { feature: 'organization-targets', name: 'Targets', icon: 'multiline_chart', section: 'Organization' },
    { feature: 'organization-dates', name: 'Date Cycles', icon: 'event', section: 'Organization' },
    {
        feature: 'organization-integrations',
        name: 'Integrations',
        icon: 'integration_instructions',
        section: 'Organization',
    },
    { feature: 'organization', name: 'Manage', icon: 'settings_applications', section: 'Organization' },
    { feature: 'organization-settings', name: 'Settings', icon: 'settings', section: 'Organization' },
];

/**
 * A bundle of pending approval records for the same user on the same position,
 * collapsed into a single review row in the user details dialog.
 */
export interface GroupedPendingApproval {
    key: string;
    positionKey: string;
    positionName: string;
    approvals: PendingPositionApproval[];
    hatNames: string[];
}

@Component({
    selector: 'shared-org-user-dialog',
    imports: [HatLocationPipe, MaterialModule, NormalizePipe, CommonModule],
    templateUrl: './org-user-details-dialog.html',
    styleUrls: ['./org-dialog.scss', '../organization-board.component.scss', './org-user-details-dialog.scss'],
})
export class OrgUserDetailsDialogComponent implements OnInit, OnDestroy {
    // Computed once in ngOnInit to avoid change detection issues
    public orgUserDetails: OrgUser | null = null;
    public userEmail: string | null = null;
    public hasSecurityGroups = false;
    public securityGroupsList: any[] = [];
    public hasFeatureDetails = false;
    public featureDetailsList: any[] = [];
    public approvedPermissionsCount = 0;
    public pendingPermissionsCount = 0;

    // Menu preview
    public menuPreview: { section: string; items: { name: string; icon: string }[] }[] = [];

    // Positions held by the user (via hats)
    public positionsList: { positionKey: string; name: string; hatName: string; hatKey: string; partType?: string }[] =
        [];
    public hasPositions = false;
    public approvedHatsCount = 0;

    // Aggregated features from all sources (direct + security groups)
    public aggregatedFeaturesList: string[] = [];
    public hasAggregatedFeatures = false;

    // Feature & permission description lookups (for tooltips)
    public featureDescriptions: Record<string, string> = {};
    public permissionDescriptions: Record<string, Record<string, string>> = {};

    // Section expanded states (for collapsible sections)
    public hatsSectionExpanded = true;
    public positionsHeldSectionExpanded = true;
    public securityGroupsSectionExpanded = true;
    public allFeaturesSectionExpanded = true;
    public featurePermissionsSectionExpanded = true;
    public expandedFeaturePanels = new Set<string>();
    public expandedSecurityGroupPanels = new Set<string>();
    public policyAckSectionExpanded = true;
    public menuPreviewSectionExpanded = true;

    // Permission Lineage Tree
    public lineageTreeControl = new NestedTreeControl<LineageTreeNode>((node) => node.children);
    public lineageDataSource = new MatTreeNestedDataSource<LineageTreeNode>();
    public hasLineageData = false;
    public lineageSectionExpanded = false; // Collapsed by default
    public showOnlyWithFeatures = true; // Only show hats with features by default
    public allLineageData: LineageTreeNode[] = []; // Full data
    public filteredLineageData: LineageTreeNode[] = []; // Filtered data

    // Policy Lineage Tree (Current Policies)
    public policyTreeControl = new NestedTreeControl<PolicyLineageNode>((node) => node.children);
    public policyDataSource = new MatTreeNestedDataSource<PolicyLineageNode>();
    public hasPolicyLineageData = false;
    public policySectionExpanded = false; // Collapsed by default

    // Historical Policy Lineage Tree
    public historicalPolicyTreeControl = new NestedTreeControl<PolicyLineageNode>((node) => node.children);
    public historicalPolicyDataSource = new MatTreeNestedDataSource<PolicyLineageNode>();
    public hasHistoricalPolicyLineageData = false;
    public historicalPolicySectionExpanded = false; // Collapsed by default
    public allPolicyLineageData: PolicyLineageNode[] = [];

    // Position Lineage Tree
    public positionTreeControl = new NestedTreeControl<PositionLineageNode>((node) => node.children);
    public positionDataSource = new MatTreeNestedDataSource<PositionLineageNode>();
    public hasPositionLineageData = false;
    public positionSectionExpanded = false; // Collapsed by default
    public allPositionLineageData: PositionLineageNode[] = [];

    // Security Group Lineage Tree
    public securityGroupTreeControl = new NestedTreeControl<SecurityGroupLineageNode>((node) => node.children);
    public securityGroupDataSource = new MatTreeNestedDataSource<SecurityGroupLineageNode>();
    public hasSecurityGroupLineageData = false;
    public securityGroupSectionExpanded = false; // Collapsed by default
    public allSecurityGroupLineageData: SecurityGroupLineageNode[] = [];

    // Menu Lineage Tree
    public menuLineageTreeControl = new NestedTreeControl<MenuLineageNode>((node) => node.children);
    public menuLineageDataSource = new MatTreeNestedDataSource<MenuLineageNode>();
    public hasMenuLineageData = false;
    public menuLineageSectionExpanded = false; // Collapsed by default
    public allMenuLineageData: MenuLineageNode[] = [];

    // Policy acknowledgments
    public isLoadingPolicies = true;
    public policyAcknowledgments: PolicyAcknowledgment[] = [];
    public userPolicies: UserPolicyItem[] = []; // All policies assigned to user via hats
    public currentPolicies: UserPolicyItem[] = []; // Currently assigned policies
    public historicalPolicies: UserPolicyItem[] = []; // Historical (no longer assigned) policies
    public pendingPoliciesCount = 0;
    public acknowledgedPoliciesCount = 0;
    public totalPoliciesCount = 0;
    public historicalPolicyAckSectionExpanded = false; // Historical section collapsed by default
    public allHistoricalPolicyLineageData: PolicyLineageNode[] = [];

    private db = inject(AngularFirestore);
    private dialogService = inject(OrgDialogService);
    private hattingService = inject(OrgHattingService);
    private profileColorService = inject(ProfileColorService);
    private destroy$ = new Subject<void>();
    private policySub?: Subscription;

    // Online presence
    public isUserOnline = false;

    // Profile color fetched from users collection
    public userProfileColor = '';
    // Note: userProfileColor kept for template binding; populated from ProfileColorService cache

    // === Pending Approval Data (computed client-side from orgHats) ===
    public hasPendingAssignments = false;
    public pendingHatsList: { hatKey: string; name: string; positionNames: string[] }[] = [];
    public pendingSecurityGroupsList: {
        sgKey: string;
        name: string;
        features: string[];
        featureDetails: Record<string, any>;
        viaHatName: string;
        viaHatKey: string;
    }[] = [];
    public pendingFeatureDetailsList: { featureKey: string; permissions: string[] }[] = [];
    public pendingFeaturesList: string[] = [];
    public pendingPositionsList: { positionKey: string; name: string; hatName: string; partType?: string }[] = [];
    public pendingPoliciesList: UserPolicyItem[] = [];
    public pendingSectionExpanded = true;
    public pendingApprovals: PendingPositionApproval[] = [];
    /**
     * Pending approvals grouped by (positionKey) — multiple records for the same
     * position (e.g. one per hat-link action) are bundled into a single review row
     * so the admin sees "Review & Approve" once per user+post, not once per record.
     */
    public groupedPendingApprovals: GroupedPendingApproval[] = [];
    public mayManageHats = false;

    // Phase B.10 — S1 invitation lifecycle. True when the user has been added
    // to the org via the wizard / invite flow but has not yet accepted (i.e.
    // `orgUsers[uid].status === 'pending'`). While true, the admin cannot
    // approve hat assignments — they must wait for the user to accept.
    public awaitingAcceptance = false;

    // === Combined lists (approved + pending, for unified display) ===
    /** ISS-021: each post held, with where it sits (and a linked Dept 19 office). */
    public get positionLines(): string[] {
        const uid = this.data?.user?.key || '';
        const org = this.data?.organization;
        if (!uid || !org) return [];
        if (this.positionLinesFor !== org || this.positionLinesUid !== uid) {
            this.positionLinesFor = org;
            this.positionLinesUid = uid;
            this.positionLinesCache = staffPositionLines(org, uid);
        }
        return this.positionLinesCache;
    }
    private positionLinesFor: unknown;
    private positionLinesUid = '';
    private positionLinesCache: string[] = [];

    public combinedHatsList: {
        hatKey: string;
        name: string;
        isPending: boolean;
        positionCount?: number;
        isBoard?: boolean;
    }[] = [];
    public combinedPositionsList: {
        positionKey: string;
        name: string;
        hatName: string;
        hatKey?: string;
        partType?: string;
        isPending: boolean;
        isBoard?: boolean;
    }[] = [];
    public combinedSecurityGroupsList: {
        sgKey: string;
        name: string;
        features: string[];
        featureDetails: Record<string, any>;
        isPending: boolean;
        viaHatName?: string;
    }[] = [];
    public combinedFeaturesList: { feature: string; isPending: boolean }[] = [];
    public combinedMenuPreview: { section: string; items: { name: string; icon: string; isPending: boolean }[] }[] = [];

    constructor(
        public dialogRef: MatDialogRef<OrgUserDetailsDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: { user: UserDetails; node: any; organization: Organization; onlineUserIds?: string[] }
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-153 item 4). Adding the
        // panel class from inside the component covers every open site (7+ of
        // them) without per-site config; the class is phone-scoped in
        // global-styles.scss, so desktop dialogs are untouched.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    public ngOnInit(): void {
        console.log('OrgUserDetailsDialogComponent > data:', this.data);
        this.computeUserDetails();
        this.computePendingDetails();
        this.buildCombinedLists();
        this.loadUserPolicies();
        this.loadUserProfileColor();

        // Check if current user has manage-org-hats permission (for approve/reject buttons)
        this.mayManageHats = this.dialogService.mayManageHats;

        // Load pending approval records for this user
        this.loadPendingApprovals();

        // Determine online status from the passed-in onlineUserIds
        const userKey = this.data.user?.key || this.data.node?.data?.key || '';
        this.isUserOnline = !!(userKey && (this.data.onlineUserIds || []).includes(userKey));

        // Subscribe to live organization updates so security group / feature
        // changes are reflected while the dialog is open.
        this.dialogService.organization$
            .pipe(
                skip(1), // skip the current value (already computed above)
                takeUntil(this.destroy$)
            )
            .subscribe((org) => {
                if (org && org.key === this.data.organization?.key) {
                    console.log('OrgUserDetailsDialog > organization updated live, recomputing...');
                    this.data.organization = org;
                    this.computeUserDetails();
                    this.computePendingDetails();
                    this.buildCombinedLists();
                    this.loadPendingApprovals();
                    this.loadUserPolicies();
                }
            });
    }

    public ngOnDestroy(): void {
        this.policySub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Gets the avatar URL for the current user.
     * Returns photoThumbnail if available, otherwise falls back to generated avatar.
     */
    public getUserAvatarUrl(): string {
        const user = this.data?.user;
        const org = this.data?.organization;

        // Check orgUserDetails first (most up-to-date)
        if (this.orgUserDetails?.photoThumbnail) {
            return this.orgUserDetails.photoThumbnail;
        }

        // Check user object passed in
        if (user?.photoThumbnail) {
            return user.photoThumbnail;
        }

        // Check organization's orgUsers
        const uid = user?.key;
        if (uid && org?.orgUsers?.[uid]?.photoThumbnail) {
            return org.orgUsers[uid].photoThumbnail!;
        }

        // Fall back to generated avatar with larger size for dialog
        const name = user?.name || 'U';
        // Use ProfileColorService cache, with fallbacks
        const uid2 = user?.key || '';
        const profileColor =
            this.profileColorService.getColor(uid2) ||
            this.orgUserDetails?.profileColor ||
            user?.profileColor ||
            '5c6bc0';
        const bgColor = profileColor.replace('#', '');
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${bgColor}&color=fff&size=120&bold=true`;
    }

    /**
     * Loads the user's profileColor from the ProfileColorService cache.
     * ensureLoaded() triggers a Firestore fetch only if the cache doesn't have it yet.
     */
    private loadUserProfileColor(): void {
        const uid = this.data?.user?.key;
        if (!uid) return;

        // Try cache first (instant — no network)
        const cached = this.profileColorService.getColor(uid);
        if (cached) {
            this.userProfileColor = cached;
            return;
        }

        // Cache miss — trigger fetch via service (it handles dedup & caching)
        this.profileColorService.ensureLoaded(uid);
    }

    private computeUserDetails(): void {
        // Get the org user details
        if (this.data?.organization?.orgUsers && this.data?.user?.key) {
            this.orgUserDetails = this.data.organization.orgUsers[this.data.user.key] || null;
        }

        // S1 invitation gate — recompute on every refresh so the banner / approve
        // button gate flip as soon as the user accepts.
        this.awaitingAcceptance = (this.orgUserDetails as any)?.status === 'pending';

        // Get email from multiple sources (orgUserDetails, passed-in user data, or node data)
        this.userEmail =
            this.orgUserDetails?.email ||
            (this.data?.user as any)?.email ||
            (this.data?.node?.data as any)?.email ||
            null;

        // If no email found locally, fetch from root users collection (ultimate source)
        if (!this.userEmail && this.data?.user?.key) {
            this.fetchEmailFromUsersCollection(this.data.user.key);
        }

        if (!this.orgUserDetails) {
            return;
        }

        const userData = this.orgUserDetails as any;
        const org = this.data.organization as any;

        // Get all security groups from the organization
        const orgSecurityGroups = org.orgSecurityGroups || {};

        console.log('=== SECURITY GROUPS DEBUG ===');
        console.log('Organization orgSecurityGroups:', orgSecurityGroups);

        // User's security groups can come from:
        // 1. securityGroupsDetails on the user (direct assignment)
        // 2. hatsDetails -> each hat has securityGroups array referencing security groups
        const securityGroupsDetails = userData.securityGroupsDetails || {};
        const hatsDetails = this.orgUserDetails.hatsDetails || {};

        // Collect all security group keys the user has access to
        const userSecurityGroupKeys = new Set<string>();

        // From direct securityGroupsDetails
        Object.keys(securityGroupsDetails).forEach((key) => userSecurityGroupKeys.add(key));

        // From hats -> securityGroups references
        for (const [hatKey, hatData] of Object.entries(hatsDetails)) {
            const hat = hatData as any;
            // Check securityGroups array (can be objects with key or just strings)
            const sgRefs = hat.securityGroups || hat.securityGroupsArray || [];
            for (const sgRef of sgRefs) {
                const sgKey = typeof sgRef === 'string' ? sgRef : sgRef?.key;
                if (sgKey) {
                    userSecurityGroupKeys.add(sgKey);
                }
            }
        }

        console.log('User security group keys:', Array.from(userSecurityGroupKeys));

        // Build the security groups list with full data
        const allSecurityGroups: Map<string, any> = new Map();

        for (const sgKey of userSecurityGroupKeys) {
            // Prefer org-level SG data (updated first by the updateSecurityGroup task)
            // over the per-user copy (only updated after the slower updateUser task completes)
            const sgData = orgSecurityGroups[sgKey] || securityGroupsDetails[sgKey];
            if (sgData) {
                console.log(`Found security group [${sgKey}]:`, sgData);
                allSecurityGroups.set(sgKey, sgData);
            } else {
                console.log(`Security group [${sgKey}] data not found`);
            }
        }

        // Convert to list
        if (allSecurityGroups.size > 0) {
            this.hasSecurityGroups = true;
            this.securityGroupsList = Array.from(allSecurityGroups.entries()).map(([sgKey, securityGroup]) => ({
                sgKey,
                name: securityGroup?.name || sgKey,
                features: securityGroup?.features || [],
                featureDetails: securityGroup?.featureDetails || {},
            }));
        }

        // Compute feature details list from user's featureDetails (aggregated permissions)
        if (this.orgUserDetails.featureDetails) {
            const entries = Object.entries(this.orgUserDetails.featureDetails);
            this.hasFeatureDetails = entries.length > 0;
            this.featureDetailsList = entries.map(([featureKey, value]) => ({
                featureKey,
                permissions: value?.permissions || [],
            }));
            const approvedPerms = new Set<string>();
            for (const fd of this.featureDetailsList) {
                for (const p of fd.permissions) approvedPerms.add(p);
            }
            this.approvedPermissionsCount = approvedPerms.size;
        }

        // Compute positions from hats - each hat can have positions assigned
        this.computePositionsFromHats();

        // Compute aggregated features from all sources (direct + security groups)
        this.computeAggregatedFeatures();

        // Build the Permission Lineage Tree
        this.buildPermissionLineageTree();

        // Build the Position Lineage Tree
        this.buildPositionLineageTree();

        // Build the Security Group Lineage Tree
        this.buildSecurityGroupLineageTree();

        // Compute menu preview based on user's features
        this.computeMenuPreview();

        // Build the Menu Lineage Tree
        this.buildMenuLineageTree();
    }

    /**
     * Compute pending hat assignment details for the current user.
     * Resolves which hats, security groups, features and positions the user
     * WOULD gain once their pending assignment is approved.
     */
    private computePendingDetails(): void {
        const org = this.data?.organization as any;
        const uid = this.data?.user?.key;
        if (!org || !uid) return;

        const orgHats: Record<string, any> = org.orgHats || {};
        const orgSecurityGroups: Record<string, any> = org.orgSecurityGroups || {};
        const orgParts: any[] = org.orgParts || [];
        const partsMap = new Map<string, any>();
        const flattenParts = (parts: any[]) => {
            for (const part of parts) {
                if (part?.key) partsMap.set(part.key, part);
                if (part?.partKey && part.partKey !== part.key) partsMap.set(part.partKey, part);
                if (part?.children?.length) flattenParts(part.children);
            }
        };
        flattenParts(orgParts);

        // Reset lists
        this.pendingHatsList = [];
        this.pendingSecurityGroupsList = [];
        this.pendingFeatureDetailsList = [];
        this.pendingFeaturesList = [];
        this.pendingPositionsList = [];

        const pendingSgKeys = new Set<string>();
        const pendingFeatureKeys = new Set<string>();

        for (const [hatKey, hat] of Object.entries(orgHats) as [string, any][]) {
            const pendingArr: string[] = hat?.pendingAssignedToArray || [];
            if (!pendingArr.includes(uid)) continue;

            // Collect position names for this hat
            const positionNames: string[] = [];
            const positions = hat?.positions || [];
            for (const pos of positions) {
                const posKey = pos?.key || pos?.partKey;
                const posName = pos?.name || partsMap.get(posKey)?.name || posKey;
                const partType = pos?.partType || partsMap.get(posKey)?.partType || undefined;
                if (posKey) {
                    positionNames.push(posName);
                    this.pendingPositionsList.push({
                        positionKey: posKey,
                        name: posName,
                        hatName: hat?.name || hatKey,
                        partType,
                    });
                }
            }
            const positionArray: string[] = hat?.positionArray || [];
            for (const posKey of positionArray) {
                if (posKey && !positions.some((p: any) => (p?.key || p?.partKey) === posKey)) {
                    const posName = partsMap.get(posKey)?.name || posKey;
                    const partType = partsMap.get(posKey)?.partType || undefined;
                    positionNames.push(posName);
                    this.pendingPositionsList.push({
                        positionKey: posKey,
                        name: posName,
                        hatName: hat?.name || hatKey,
                        partType,
                    });
                }
            }

            this.pendingHatsList.push({ hatKey, name: hat?.name || hatKey, positionNames });

            // Resolve security groups for this hat
            const sgRefs = hat?.securityGroups || hat?.securityGroupsArray || [];
            for (const sgRef of sgRefs) {
                const sgKey = typeof sgRef === 'string' ? sgRef : sgRef?.key;
                if (!sgKey || pendingSgKeys.has(sgKey)) continue;
                pendingSgKeys.add(sgKey);

                const sgData = orgSecurityGroups[sgKey];
                if (sgData) {
                    const features = sgData.features || [];
                    features.forEach((f: string) => pendingFeatureKeys.add(f));
                    this.pendingSecurityGroupsList.push({
                        sgKey,
                        name: sgData.name || sgKey,
                        features,
                        featureDetails: sgData.featureDetails || {},
                        viaHatName: hat?.name || hatKey,
                        viaHatKey: hatKey,
                    });
                }
            }
        }

        this.pendingFeaturesList = Array.from(pendingFeatureKeys).sort();

        // Build pending feature details from security group featureDetails
        const featurePermMap = new Map<string, Set<string>>();
        for (const sg of this.pendingSecurityGroupsList) {
            for (const [fKey, fVal] of Object.entries(sg.featureDetails || {})) {
                if (!featurePermMap.has(fKey)) featurePermMap.set(fKey, new Set());
                const perms: string[] = (fVal as any)?.permissions || [];
                perms.forEach((p) => featurePermMap.get(fKey)!.add(p));
            }
        }
        this.pendingFeatureDetailsList = Array.from(featurePermMap.entries()).map(([featureKey, permsSet]) => ({
            featureKey,
            permissions: Array.from(permsSet),
        }));
        const pendingPerms = new Set<string>();
        for (const fd of this.pendingFeatureDetailsList) {
            for (const p of fd.permissions) pendingPerms.add(p);
        }
        this.pendingPermissionsCount = pendingPerms.size;

        this.hasPendingAssignments = this.pendingHatsList.length > 0;

        // Compute pending policies: published policies that target pending hats
        const pendingHatKeySet = new Set(this.pendingHatsList.map((h) => h.hatKey));
        const approvedHatKeySet = new Set(this.getApprovedHatKeys());
        const orgPolicies: Record<string, any> = org.orgPolicies || {};
        const policyKeys: string[] = orgPolicies['policiesKeys'] || [];
        const allPendingHatNames = this.pendingHatsList.map((h) => h.name);

        this.pendingPoliciesList = [];
        for (const policyKey of policyKeys) {
            const policy = orgPolicies[policyKey];
            if (!policy || policy.status !== 'published' || policy.isArchived) continue;

            const targetMode: string = policy.targets?.mode || '';
            const resolvedHatKeys: string[] = policy.targets?.resolvedHatKeys || [];

            // Check if this policy would target the pending hats:
            // 1. 'all_organization' mode targets every hat including new/pending ones
            // 2. Otherwise check if resolvedHatKeys explicitly includes a pending hat
            const isOrgWide = targetMode === 'all_organization';
            const matchingPendingHats = isOrgWide
                ? Array.from(pendingHatKeySet)
                : resolvedHatKeys.filter((hk: string) => pendingHatKeySet.has(hk));

            if (matchingPendingHats.length === 0) continue;

            // Skip if user already gets this policy via an approved hat
            const alreadyViaApproved = isOrgWide
                ? approvedHatKeySet.size > 0
                : resolvedHatKeys.some((hk: string) => approvedHatKeySet.has(hk));
            if (alreadyViaApproved) continue;

            const receivedViaHats = isOrgWide
                ? allPendingHatNames
                : matchingPendingHats.map((hk: string) => {
                      const hat = orgHats[hk];
                      return (hat as any)?.name || hk;
                  });

            this.pendingPoliciesList.push({
                policyKey,
                policyName: policy.name || policyKey,
                policy,
                receivedViaHats,
                status: policy.requiresAcknowledgment ? 'pending' : 'not-required',
                isCurrentlyAssigned: true,
                isPending: true,
            });
        }
    }

    /**
     * Build combined lists that merge approved and pending items for unified display.
     * Each item carries an `isPending` flag so the template can style them differently.
     */
    private buildCombinedLists(): void {
        const org = this.data.organization as any;
        const orgHats = org?.orgHats || {};
        const approvedHatKeys = this.getApprovedHatKeys();

        // --- Hats ---
        const approvedHats = approvedHatKeys.map((hatKey) => ({
            hatKey,
            name: this.orgUserDetails?.hatsDetails?.[hatKey]?.name || orgHats?.[hatKey]?.name || hatKey,
            isPending: false,
            positionCount: 0,
        }));
        const pendingHats = this.pendingHatsList.map((h) => ({
            hatKey: h.hatKey,
            name: h.name,
            isPending: true,
            positionCount: h.positionNames.length,
        }));
        // Avoid duplicates (a hat could theoretically appear in both)
        const hatKeys = new Set(approvedHats.map((h) => h.hatKey));
        this.combinedHatsList = [...approvedHats, ...pendingHats.filter((h) => !hatKeys.has(h.hatKey))];
        this.approvedHatsCount = approvedHats.length;

        // --- Positions (posts only) ---
        const approvedPositions = this.positionsList
            .filter((p) => p.partType === 'post')
            .map((p) => ({ ...p, isPending: false }));
        const pendingPositions = this.pendingPositionsList
            .filter((p) => p.partType === 'post')
            .map((p) => ({ ...p, isPending: true }));
        const posKeys = new Set(approvedPositions.map((p) => p.positionKey));
        this.combinedPositionsList = [
            ...approvedPositions,
            ...pendingPositions.filter((p) => !posKeys.has(p.positionKey)),
        ];

        // Board of Directors seats wear the same amber as the part editor's BOARD rows (Paul, 2026-10-21)
        const boardSeats = boardSeatPostKeys(org?.orgParts);
        this.combinedPositionsList = this.combinedPositionsList.map((p) => ({
            ...p,
            isBoard: boardSeats.has(p.positionKey),
        }));
        this.combinedHatsList = this.combinedHatsList.map((h) => ({
            ...h,
            isBoard:
                ((orgHats?.[h.hatKey] as any)?.positionArray || []).some((k: string) => boardSeats.has(k)) ||
                this.combinedPositionsList.some((p) => p.isBoard && p.hatKey === h.hatKey),
        }));

        // --- Security Groups ---
        const approvedSGs = this.securityGroupsList.map((sg) => ({ ...sg, isPending: false }));
        const pendingSGs = this.pendingSecurityGroupsList.map((sg) => ({ ...sg, isPending: true }));
        const sgKeys = new Set(approvedSGs.map((sg) => sg.sgKey));
        this.combinedSecurityGroupsList = [...approvedSGs, ...pendingSGs.filter((sg) => !sgKeys.has(sg.sgKey))];

        // --- Features ---
        const approvedFeatureSet = new Set(this.aggregatedFeaturesList);
        const approvedFeatures = this.aggregatedFeaturesList.map((f) => ({ feature: f, isPending: false }));
        const pendingOnly = this.pendingFeaturesList.filter((f) => !approvedFeatureSet.has(f));
        const pendingFeatures = pendingOnly.map((f) => ({ feature: f, isPending: true }));
        this.combinedFeaturesList = [...approvedFeatures, ...pendingFeatures];

        // --- Menu Preview (including pending features) ---
        this.buildCombinedMenuPreview();
    }

    /**
     * Build the combined menu preview that includes both approved and pending features.
     * Items only accessible via pending features are marked with isPending = true.
     */
    private buildCombinedMenuPreview(): void {
        const approvedFeatures = new Set<string>();
        const allFeatures = new Set<string>();

        // Approved features
        const userFeatures = this.orgUserDetails?.features || [];
        userFeatures.forEach((f) => {
            approvedFeatures.add(f);
            allFeatures.add(f);
        });
        for (const sg of this.securityGroupsList) {
            for (const feature of sg.features || []) {
                approvedFeatures.add(feature);
                allFeatures.add(feature);
            }
        }

        // Pending features
        this.pendingFeaturesList.forEach((f) => allFeatures.add(f));

        // System route inheritance
        if (ORG_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) allFeatures.add('organization');
        if (MGMT_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) allFeatures.add('management-tools-dashboard');

        const approvedWithInheritance = new Set(approvedFeatures);
        if (ORG_SYSTEM_FEATURES.some((f) => approvedWithInheritance.has(f)))
            approvedWithInheritance.add('organization');
        if (MGMT_SYSTEM_FEATURES.some((f) => approvedWithInheritance.has(f)))
            approvedWithInheritance.add('management-tools-dashboard');

        // Build items with isPending flag
        const accessibleItems = MENU_ITEMS.filter((item) => allFeatures.has(item.feature)).map((item) => ({
            ...item,
            isPending: !approvedWithInheritance.has(item.feature),
        }));

        // Group by section
        const grouped = new Map<string, { name: string; icon: string; isPending: boolean }[]>();
        for (const item of accessibleItems) {
            if (!grouped.has(item.section)) grouped.set(item.section, []);
            const existing = grouped.get(item.section)!;
            if (!existing.some((e) => e.name === item.name)) {
                existing.push({ name: item.name, icon: item.icon, isPending: item.isPending });
            }
        }

        const sectionOrder = ['Home', 'Workspace', 'Management Tools', 'Organization'];
        this.combinedMenuPreview = sectionOrder
            .filter((section) => grouped.has(section))
            .map((section) => ({ section, items: grouped.get(section)! }));
    }

    /**
     * Loads pending approval records for this user from the pendingApprovals subcollection.
     */
    private async loadPendingApprovals(): Promise<void> {
        const orgKey = this.data.organization?.key;
        const userKey = this.data.user?.key;
        if (!orgKey || !userKey) return;

        const allPending = await this.hattingService.getPendingApprovals(orgKey, 'pending');
        this.pendingApprovals = allPending.filter((a) => a.uid === userKey);
        this.groupedPendingApprovals = this.buildGroupedPendingApprovals(this.pendingApprovals);
    }

    /**
     * Group pending approvals by positionKey so the UI shows one "Review & Approve"
     * row per (user, position) rather than one row per approval record.
     */
    private buildGroupedPendingApprovals(approvals: PendingPositionApproval[]): GroupedPendingApproval[] {
        const org = this.data.organization as any;
        const orgHats = org?.orgHats || {};
        const byPos = new Map<string, PendingPositionApproval[]>();
        for (const a of approvals) {
            const posKey = a.positionKey || '';
            const arr = byPos.get(posKey) || [];
            arr.push(a);
            byPos.set(posKey, arr);
        }
        const groups: GroupedPendingApproval[] = [];
        byPos.forEach((recs, posKey) => {
            // Union hat names across all records in this group (de-duped).
            const seen = new Set<string>();
            const hatNames: string[] = [];
            for (const r of recs) {
                const keys = Array.isArray(r.hatKeys) ? r.hatKeys : [];
                const specialKeys = Array.isArray((r as any).specialRightsHatKeys)
                    ? (r as any).specialRightsHatKeys
                    : [];
                for (const k of [...keys, ...specialKeys]) {
                    if (seen.has(k)) continue;
                    seen.add(k);
                    hatNames.push(orgHats?.[k]?.name || k);
                }
            }
            groups.push({
                key: posKey || 'special-rights',
                positionKey: posKey,
                positionName: recs[0]?.positionName || 'Org Part Assignment',
                approvals: recs,
                hatNames,
            });
        });
        return groups;
    }

    /**
     * Resolve the display names of the hats referenced by a pending approval record,
     * so the UI can show which hats are being requested (not just a count).
     *
     * Falls back to the raw hatKey if the org cache does not yet contain the hat doc.
     */
    public getApprovalHatNames(approval: PendingPositionApproval): string[] {
        const org = this.data.organization as any;
        const orgHats = org?.orgHats || {};
        const keys: string[] = Array.isArray(approval?.hatKeys) ? approval.hatKeys : [];
        const specialKeys: string[] = Array.isArray((approval as any)?.specialRightsHatKeys)
            ? (approval as any).specialRightsHatKeys
            : [];
        const allKeys = Array.from(new Set([...keys, ...specialKeys]));
        return allKeys.map((k) => orgHats?.[k]?.name || k);
    }

    /**
     * Opens the approval dialog for a pending approval record.
     */
    public async openApprovalDialog(approval: PendingPositionApproval): Promise<void> {
        const result = await this.dialogService.openApproveAssignmentDialog(approval);
        if (result?.action === 'approved' || result?.action === 'rejected') {
            await this.refreshDialogData();
        }
    }

    /**
     * Opens the approval dialog for a group of pending approval records
     * (same user + same position). The dialog merges all hats across records
     * into a single checkbox list.
     */
    public async openGroupedApprovalDialog(group: { approvals: PendingPositionApproval[] }): Promise<void> {
        if (!group?.approvals?.length) return;
        const result = await this.dialogService.openApproveAssignmentDialog(group.approvals);
        if (result?.action === 'approved' || result?.action === 'rejected') {
            await this.refreshDialogData();
        }
    }

    /**
     * Hard refresh this dialog's data after approve/reject so UI reflects latest org state
     * without requiring close/reopen.
     */
    private async refreshDialogData(): Promise<void> {
        const orgKey = this.data.organization?.key;
        if (!orgKey) return;

        const latestOrg = await firstValueFrom(
            this.db.collection('organizations').doc<Organization>(orgKey).valueChanges().pipe(take(1))
        );

        if (latestOrg) {
            this.data.organization = { ...(latestOrg as any), key: (latestOrg as any).key || orgKey } as Organization;
            this.dialogService.organization = this.data.organization;
        }

        this.computeUserDetails();
        this.computePendingDetails();
        this.buildCombinedLists();
        await this.loadPendingApprovals();
        this.loadUserPolicies();
    }

    /**
     * Compute aggregated features from direct user features AND security groups
     * This ensures we show all features even if orgUserDetails.features is empty
     */
    private computeAggregatedFeatures(): void {
        const allFeatures = new Set<string>();

        // Add direct user features
        const userFeatures = this.orgUserDetails?.features || [];
        userFeatures.forEach((f) => allFeatures.add(f));

        // Add features from security groups
        for (const sg of this.securityGroupsList) {
            for (const feature of sg.features || []) {
                allFeatures.add(feature);
            }
        }

        this.aggregatedFeaturesList = Array.from(allFeatures).sort();
        this.hasAggregatedFeatures = this.aggregatedFeaturesList.length > 0;

        // Build feature & permission description lookups for tooltips
        const org = this.data.organization as any;
        const orgFeatures = org?.orgFeatures || {};
        for (const fKey of this.aggregatedFeaturesList) {
            const orgFeature = orgFeatures[fKey];
            if (orgFeature?.description) this.featureDescriptions[fKey] = orgFeature.description;
            if (orgFeature?.permissionDescriptions)
                this.permissionDescriptions[fKey] = orgFeature.permissionDescriptions;
        }

        console.log('=== AGGREGATED FEATURES DEBUG ===');
        console.log('Direct features:', userFeatures);
        console.log(
            'From security groups:',
            this.securityGroupsList.flatMap((sg) => sg.features || [])
        );
        console.log('Aggregated total:', this.aggregatedFeaturesList);
    }

    /**
     * Extract positions from the user's hats
     * Each hat can have positions array or positionArray
     */
    private computePositionsFromHats(): void {
        const hatsDetails = this.orgUserDetails?.hatsDetails || {};
        const org = this.data.organization as any;
        const orgParts = org?.orgParts || [];

        // Build a map of orgParts for quick lookup (flatten tree to include children)
        const partsMap = new Map<string, any>();
        const flattenParts = (parts: any[]) => {
            for (const part of parts) {
                if (part?.key) partsMap.set(part.key, part);
                if (part?.partKey && part.partKey !== part.key) partsMap.set(part.partKey, part);
                if (part?.children?.length) flattenParts(part.children);
            }
        };
        flattenParts(orgParts);

        // Also check organization.orgHats for full hat data with positions
        const orgHats = org?.orgHats || {};
        const approvedHatKeys = this.getApprovedHatKeys();

        // Track unique positions to avoid duplicates
        const positionsMap = new Map<
            string,
            { positionKey: string; name: string; hatName: string; hatKey: string; partType?: string }
        >();

        for (const hatKey of approvedHatKeys) {
            const hat = (hatsDetails as any)?.[hatKey] || {};
            const fullHatData = orgHats[hatKey] || {};
            const hatName = hat?.name || fullHatData?.name || hatKey;

            // Get positions from positions array (array of position objects)
            const positions = hat?.positions || fullHatData?.positions || [];
            for (const pos of positions) {
                const posKey = pos?.key || pos?.partKey;
                if (posKey && !positionsMap.has(posKey)) {
                    const partData = partsMap.get(posKey);
                    positionsMap.set(posKey, {
                        positionKey: posKey,
                        name: pos?.name || partData?.name || posKey,
                        hatName,
                        hatKey,
                        partType: pos?.partType || partData?.partType || undefined,
                    });
                }
            }

            // Also check positionArray (array of position keys)
            const positionArray = hat?.positionArray || fullHatData?.positionArray || [];
            for (const posKey of positionArray) {
                if (posKey && !positionsMap.has(posKey)) {
                    const partData = partsMap.get(posKey);
                    positionsMap.set(posKey, {
                        positionKey: posKey,
                        name: partData?.name || posKey,
                        hatName,
                        hatKey,
                        partType: partData?.partType || undefined,
                    });
                }
            }

            // Also check orgPosition (single position object - legacy)
            const orgPosition = hat?.orgPosition || fullHatData?.orgPosition;
            if (orgPosition?.key && !positionsMap.has(orgPosition.key)) {
                positionsMap.set(orgPosition.key, {
                    positionKey: orgPosition.key,
                    name: orgPosition?.name || orgPosition.key,
                    hatName,
                    hatKey,
                });
            }
        }

        this.positionsList = Array.from(positionsMap.values());
        this.hasPositions = this.positionsList.length > 0;

        console.log('=== POSITIONS DEBUG ===');
        console.log('Positions found:', this.positionsList);
    }

    private computeMenuPreview(): void {
        // Aggregate features from BOTH direct user features AND security groups
        const allFeatures = new Set<string>();

        // Add direct user features
        const userFeatures = this.orgUserDetails?.features || [];
        userFeatures.forEach((f) => allFeatures.add(f));

        // Add features from security groups
        for (const sg of this.securityGroupsList) {
            for (const feature of sg.features || []) {
                allFeatures.add(feature);
            }
        }

        // Handle system route inheritance: if ANY org feature is present, add 'organization' parent
        // This mirrors the system route behavior where My Hats, My Policies, Org Board, Dashboard, and Manage
        // are accessible with any organization-* feature
        if (ORG_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) {
            allFeatures.add('organization');
        }

        // Handle management tools system route: if ANY mgmt feature is present, add 'management-tools-dashboard'
        // This mirrors the system route behavior where Dashboard is accessible with any mgmt feature
        if (MGMT_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) {
            allFeatures.add('management-tools-dashboard');
        }

        // Filter menu items based on all user's features
        const accessibleItems = MENU_ITEMS.filter((item) => allFeatures.has(item.feature));

        // Group by section
        const grouped = new Map<string, { name: string; icon: string }[]>();
        for (const item of accessibleItems) {
            if (!grouped.has(item.section)) {
                grouped.set(item.section, []);
            }
            // Avoid duplicates by name
            const existing = grouped.get(item.section)!;
            if (!existing.some((e) => e.name === item.name)) {
                existing.push({ name: item.name, icon: item.icon });
            }
        }

        // Convert to array with proper section ordering
        const sectionOrder = ['Home', 'Workspace', 'Management Tools', 'Organization'];
        this.menuPreview = sectionOrder
            .filter((section) => grouped.has(section))
            .map((section) => ({
                section,
                items: grouped.get(section)!,
            }));
    }

    /**
     * Fetch email from the organization's users subcollection (/organizations/{orgId}/users/{uid})
     * This is the source that org members can access (root /users collection may be restricted)
     */
    private fetchEmailFromUsersCollection(uid: string): void {
        const orgId = this.data?.organization?.key;
        if (!orgId) {
            console.warn('No organization key available to fetch user email');
            return;
        }

        // First try org users subcollection (accessible to org members)
        this.db
            .collection('organizations')
            .doc(orgId)
            .collection('users')
            .doc(uid)
            .get()
            .subscribe({
                next: (doc) => {
                    if (doc.exists) {
                        const userData = doc.data() as any;
                        this.userEmail = userData?.email || null;
                        console.log('Fetched email from org users subcollection:', this.userEmail);
                    } else {
                        // Fallback: try root users collection (may be restricted by security rules)
                        this.fetchEmailFromRootUsersCollection(uid);
                    }
                },
                error: (err) => {
                    console.warn('Could not fetch email from org users subcollection:', err);
                    // Fallback: try root users collection
                    this.fetchEmailFromRootUsersCollection(uid);
                },
            });
    }

    /**
     * Fallback: Fetch email from root /users collection (may be restricted)
     */
    private fetchEmailFromRootUsersCollection(uid: string): void {
        this.db
            .collection('users')
            .doc(uid)
            .get()
            .subscribe({
                next: (doc) => {
                    if (doc.exists) {
                        const userData = doc.data() as any;
                        this.userEmail = userData?.email || null;
                        console.log('Fetched email from root users collection:', this.userEmail);
                    }
                },
                error: (err) => {
                    console.warn('Could not fetch email from root users collection:', err);
                },
            });
    }

    /**
     * Load policies for this user
     * Uses policyAcknowledgments as source of truth (not hat.policyKeys) to avoid
     * org-wide hat updates triggering task engine when policies are published.
     */
    private loadUserPolicies(): void {
        const org = this.data.organization;
        const userKey = this.data.user?.key;

        if (!org?.key || !userKey) {
            console.warn('Missing orgKey or userKey for loading policies');
            this.isLoadingPolicies = false;
            return;
        }

        // Subscribe to acknowledgments as source of truth
        this.policySub?.unsubscribe();
        this.policySub = this.db
            .collection('organizations')
            .doc(org.key)
            .collection<PolicyAcknowledgment>('policyAcknowledgments', (ref) => ref.where('uid', '==', userKey))
            .valueChanges()
            .subscribe({
                next: (acknowledgments) => {
                    this.policyAcknowledgments = acknowledgments;

                    const latestOrg = this.data.organization;
                    const orgHats = latestOrg?.orgHats || {};
                    const orgPolicies = latestOrg?.orgPolicies || {};
                    const userHatKeys = this.getApprovedHatKeys();
                    const policyKeys: string[] = (orgPolicies as any)['policiesKeys'] || [];

                    // Build policy items from acknowledgments (dedupe by policyKey)
                    const seenPolicies = new Set<string>();
                    const policyItems: UserPolicyItem[] = [];

                    for (const ack of acknowledgments) {
                        if (seenPolicies.has(ack.policyKey)) continue;
                        seenPolicies.add(ack.policyKey);

                        const policy = orgPolicies[ack.policyKey] as OrgPolicy | undefined;
                        const targetMode = (policy?.targets as any)?.mode || '';
                        const isOrgWide = targetMode === 'all_organization';

                        // Find which of user's hats this policy was received through
                        const receivedViaHats: string[] = [];
                        if (isOrgWide) {
                            // Org-wide policy applies to all hats — list user's hats as source
                            for (const hatKey of userHatKeys) {
                                const hat = orgHats[hatKey] as OrgHat | undefined;
                                receivedViaHats.push(hat?.name || hatKey);
                            }
                        } else {
                            for (const hatKey of userHatKeys) {
                                if (policy?.targets?.resolvedHatKeys?.includes(hatKey)) {
                                    const hat = orgHats[hatKey] as OrgHat | undefined;
                                    receivedViaHats.push(hat?.name || hatKey);
                                }
                            }
                        }

                        // Check if user is currently assigned to this policy via a hat
                        const isCurrentlyAssigned = isOrgWide ? userHatKeys.length > 0 : receivedViaHats.length > 0;

                        // Skip unpublished policies that aren't currently assigned
                        // (show historical acknowledged policies even if policy is no longer published)
                        if (policy?.status !== 'published' && !ack.isComplete) continue;

                        // Determine status
                        let status: 'acknowledged' | 'pending' | 'not-required' = 'not-required';
                        if (policy?.requiresAcknowledgment) {
                            status = ack?.isComplete && !ack.requiresReacknowledgment ? 'acknowledged' : 'pending';
                        }

                        policyItems.push({
                            policyKey: ack.policyKey,
                            policyName: policy?.name || ack.policyName,
                            policy,
                            receivedViaHats,
                            status,
                            acknowledgment: ack,
                            isCurrentlyAssigned,
                        });
                    }

                    // Supplement: find published policies that target this user's approved hats
                    // but don't have acknowledgment records yet (e.g., just approved, cron hasn't run)
                    if (userHatKeys.length > 0) {
                        const approvedHatKeySet = new Set(userHatKeys as string[]);
                        for (const pk of policyKeys) {
                            if (seenPolicies.has(pk)) continue;
                            const policy = orgPolicies[pk] as OrgPolicy | undefined;
                            if (!policy || policy.status !== 'published' || (policy as any).isArchived) continue;

                            const targetMode = (policy.targets as any)?.mode || '';
                            const isOrgWide = targetMode === 'all_organization';
                            const resolvedHatKeys: string[] = policy.targets?.resolvedHatKeys || [];

                            const appliesViaApproved =
                                isOrgWide || resolvedHatKeys.some((hk) => approvedHatKeySet.has(hk));
                            if (!appliesViaApproved) continue;

                            const receivedViaHats = isOrgWide
                                ? userHatKeys.map((hk) => (orgHats[hk] as OrgHat | undefined)?.name || hk)
                                : resolvedHatKeys
                                      .filter((hk) => approvedHatKeySet.has(hk))
                                      .map((hk) => (orgHats[hk] as OrgHat | undefined)?.name || hk);

                            seenPolicies.add(pk);
                            policyItems.push({
                                policyKey: pk,
                                policyName: policy.name || pk,
                                policy,
                                receivedViaHats,
                                status: policy.requiresAcknowledgment ? 'pending' : 'not-required',
                                isCurrentlyAssigned: true,
                            });
                        }
                    }

                    // Sort: currently assigned first, then by status (pending, acknowledged, not-required)
                    policyItems.sort((a, b) => {
                        // First sort by currently assigned
                        if (a.isCurrentlyAssigned !== b.isCurrentlyAssigned) {
                            return a.isCurrentlyAssigned ? -1 : 1;
                        }
                        // Then sort by status
                        const order = { pending: 0, acknowledged: 1, 'not-required': 2 };
                        return order[a.status] - order[b.status];
                    });

                    this.userPolicies = policyItems;

                    // Separate current and historical policies
                    this.currentPolicies = policyItems.filter((p) => p.isCurrentlyAssigned);
                    this.historicalPolicies = policyItems.filter((p) => !p.isCurrentlyAssigned);

                    // Merge pending policies from pending hat assignments
                    this.mergePendingIntoPolicies();

                    this.isLoadingPolicies = false;

                    // Build the Policy Lineage Trees separately for current and historical
                    this.buildPolicyLineageTree(this.currentPolicies);
                    this.buildHistoricalPolicyLineageTree(this.historicalPolicies);
                },
                error: (err) => {
                    console.error('Error loading policy acknowledgments:', err);
                    this.isLoadingPolicies = false;
                },
            });
    }

    /**
     * Resolve approved hats from both denormalized user cache and orgHats.assignedToArray.
     * orgHats is updated immediately on approve/reject, so this avoids stale user detail lag.
     */
    private getApprovedHatKeys(): string[] {
        const org = this.data?.organization as any;
        const uid = this.data?.user?.key;
        if (!uid) return [];

        const fromUser = ((this.orgUserDetails?.hats || []) as string[]).filter(Boolean);
        const fromOrgHats = Object.entries(org?.orgHats || {})
            .filter(([_, hat]) => ((hat as any)?.assignedToArray || []).includes(uid))
            .map(([hatKey]) => hatKey)
            .filter(Boolean);

        return Array.from(new Set([...fromUser, ...fromOrgHats]));
    }

    /**
     * Merge pending policies (from pending hat assignments) into the currentPolicies display.
     * Called after loadUserPolicies() fires AND after live org updates recompute pending data.
     */
    private mergePendingIntoPolicies(): void {
        this.currentPolicies = this.userPolicies.filter((p) => p.isCurrentlyAssigned);
        const currentPolicyKeys = new Set(this.currentPolicies.map((p) => p.policyKey));
        const pendingToAdd = this.pendingPoliciesList.filter((p) => !currentPolicyKeys.has(p.policyKey));
        this.currentPolicies = [...this.currentPolicies, ...pendingToAdd];

        this.totalPoliciesCount = this.currentPolicies.length;
        this.pendingPoliciesCount = this.currentPolicies.filter((p) => p.status === 'pending' || p.isPending).length;
        this.acknowledgedPoliciesCount = this.currentPolicies.filter((p) => p.status === 'acknowledged').length;
    }

    /**
     * Format timestamp for display
     */
    public formatDate(timestamp: firebase.firestore.Timestamp | null | undefined): string {
        if (!timestamp) return 'N/A';
        try {
            return timestamp.toDate().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return 'N/A';
        }
    }

    /**
     * Build the Permission Lineage Tree showing:
     * User → Hats → (Positions + Security Groups) → Features → Permissions
     */
    private buildPermissionLineageTree(): void {
        const hatsDetails = this.orgUserDetails?.hatsDetails || {};
        const org = this.data.organization as any;
        const orgHats = org?.orgHats || {};
        const orgSecurityGroups = org?.orgSecurityGroups || {};

        const treeData: LineageTreeNode[] = [];

        for (const [hatKey, hatData] of Object.entries(hatsDetails)) {
            const hat = hatData as any;
            const fullHatData = orgHats[hatKey] || {};
            const hatName = hat?.name || fullHatData?.name || hatKey;

            const hatNode: LineageTreeNode = {
                name: hatName,
                type: 'hat',
                icon: 'school',
                key: hatKey,
                children: [],
            };

            // Add positions under this hat
            const positions = hat?.positions || fullHatData?.positions || [];
            const positionArray = hat?.positionArray || fullHatData?.positionArray || [];
            const orgPosition = hat?.orgPosition || fullHatData?.orgPosition;

            const addedPositionKeys = new Set<string>();

            // Get orgParts for hierarchy lookup
            const orgParts = org?.orgParts || [];
            console.log(`Hat "${hatName}" - orgParts count:`, orgParts.length);
            if (orgParts.length > 0) {
                console.log('Sample orgPart:', orgParts[0]);
            }

            for (const pos of positions) {
                const posKey = pos?.key || pos?.partKey;
                if (posKey && !addedPositionKeys.has(posKey)) {
                    addedPositionKeys.add(posKey);
                    hatNode.children!.push({
                        name: pos?.name || posKey,
                        type: 'position',
                        icon: 'work',
                        key: posKey,
                        hierarchy: this.formatPositionHierarchy(posKey, orgParts),
                    });
                }
            }

            for (const posKey of positionArray) {
                if (posKey && !addedPositionKeys.has(posKey)) {
                    addedPositionKeys.add(posKey);
                    // Try to get the position name from orgParts
                    const partData = orgParts.find((p: any) => (p.key || p.partKey) === posKey);
                    hatNode.children!.push({
                        name: partData?.name || posKey,
                        type: 'position',
                        icon: 'work',
                        key: posKey,
                        hierarchy: this.formatPositionHierarchy(posKey, orgParts),
                    });
                }
            }

            if (orgPosition?.key && !addedPositionKeys.has(orgPosition.key)) {
                hatNode.children!.push({
                    name: orgPosition?.name || orgPosition.key,
                    type: 'position',
                    icon: 'work',
                    key: orgPosition.key,
                    hierarchy: this.formatPositionHierarchy(orgPosition.key, orgParts),
                });
            }

            // Add security groups under this hat
            const securityGroupsArray = hat?.securityGroupsArray || fullHatData?.securityGroupsArray || [];
            const securityGroups = hat?.securityGroups || fullHatData?.securityGroups || [];

            const sgKeys = new Set<string>();

            // Collect SG keys from both arrays
            for (const sg of [...securityGroupsArray, ...securityGroups]) {
                const sgKey = typeof sg === 'string' ? sg : sg?.key;
                if (sgKey) sgKeys.add(sgKey);
            }

            for (const sgKey of sgKeys) {
                const sgData = orgSecurityGroups[sgKey];
                if (!sgData) continue;

                const sgNode: LineageTreeNode = {
                    name: sgData?.name || sgKey,
                    type: 'security-group',
                    icon: 'security',
                    key: sgKey,
                    children: [],
                };

                // Add features under this security group
                const features = sgData?.features || [];
                const featureDetails = sgData?.featureDetails || {};

                for (const featureKey of features) {
                    const featureNode: LineageTreeNode = {
                        name: featureKey,
                        type: 'feature',
                        icon: 'star',
                        key: featureKey,
                        children: [],
                    };

                    // Add permissions under this feature
                    const permissions = featureDetails[featureKey]?.permissions || [];
                    for (const perm of permissions) {
                        featureNode.children!.push({
                            name: perm,
                            type: 'permission',
                            icon: 'check_circle',
                            key: perm,
                        });
                    }

                    sgNode.children!.push(featureNode);
                }

                hatNode.children!.push(sgNode);
            }

            treeData.push(hatNode);
        }

        // Store all data and apply filter
        this.allLineageData = treeData;
        this.hasLineageData = treeData.length > 0;
        this.applyLineageFilter();

        console.log('=== PERMISSION LINEAGE TREE ===');
        console.log('Tree data:', treeData);
        // Debug: Log position hierarchy
        for (const hat of treeData) {
            for (const child of hat.children || []) {
                if (child.type === 'position') {
                    console.log(`Position "${child.name}" hierarchy:`, child.hierarchy);
                }
            }
        }
    }

    /**
     * Apply filter to show/hide hats without features
     */
    public applyLineageFilter(): void {
        if (this.showOnlyWithFeatures) {
            // Filter to only show hats that have security groups with features
            this.filteredLineageData = this.allLineageData.filter((hatNode) => {
                return hatNode.children?.some(
                    (child) => child.type === 'security-group' && child.children && child.children.length > 0
                );
            });
        } else {
            this.filteredLineageData = this.allLineageData;
        }

        this.lineageDataSource.data = this.filteredLineageData;

        // Update tree control and expand nodes
        if (this.filteredLineageData.length > 0) {
            this.lineageTreeControl.dataNodes = this.filteredLineageData;
            this.expandAllNodes(this.filteredLineageData);
        }
    }

    /**
     * Toggle the show only with features filter
     */
    public toggleShowOnlyWithFeatures(): void {
        this.showOnlyWithFeatures = !this.showOnlyWithFeatures;
        this.applyLineageFilter();
    }

    /**
     * Recursively expand all tree nodes
     */
    private expandAllNodes(nodes: LineageTreeNode[]): void {
        for (const node of nodes) {
            this.lineageTreeControl.expand(node);
            if (node.children?.length) {
                this.expandAllNodes(node.children);
            }
        }
    }

    /**
     * Get the hierarchy chain for a position (from position up to root)
     * Returns array like: ['Department 4', 'Division 2', 'CEO'] from child to root
     */
    private getPositionHierarchyChain(positionKey: string, orgParts: any[]): string[] {
        if (!orgParts || !positionKey) return [];

        // Build lookup maps - try multiple possible key fields
        const partsMapByKey = new Map<string, any>();
        const partsMapByPartKey = new Map<string, any>();
        const partsMapByName = new Map<string, any>();

        for (const part of orgParts) {
            if (part.key) partsMapByKey.set(part.key, part);
            if (part.partKey) partsMapByPartKey.set(part.partKey, part);
            if (part.name) partsMapByName.set(part.name, part);
        }

        // Try to find the starting part using different key fields
        let startPart = partsMapByKey.get(positionKey) || partsMapByPartKey.get(positionKey);

        // If not found by key, try by name (for positions like "Department 4")
        if (!startPart) {
            startPart = partsMapByName.get(positionKey);
        }

        // Debug: Log all orgPart keys to understand the structure
        if (!startPart && orgParts.length > 0) {
            console.log(
                `Position "${positionKey}" NOT FOUND in orgParts. Sample keys:`,
                orgParts.slice(0, 3).map((p: any) => ({ key: p.key, partKey: p.partKey, name: p.name }))
            );
        }

        if (!startPart) return [];

        const chain: string[] = [];
        let currentPart = startPart;
        const visited = new Set<string>(); // Prevent infinite loops

        // Walk up the hierarchy - skip the starting position
        let isFirst = true;
        while (currentPart) {
            const currentKey = currentPart.key || currentPart.partKey || currentPart.name;
            if (visited.has(currentKey)) break;
            visited.add(currentKey);

            // Don't add the starting position
            if (!isFirst) {
                chain.push(currentPart.name || currentKey);
            }
            isFirst = false;

            // Move to parent using multiple possible field names
            const parentKey = currentPart.parentPartKey || currentPart.parentRef || currentPart.parent || '';
            if (!parentKey) break;

            currentPart = partsMapByKey.get(parentKey) || partsMapByPartKey.get(parentKey);
        }

        return chain;
    }

    /**
     * Format position hierarchy as a breadcrumb string
     * Returns: "Division 2 → CEO" (parent chain from immediate parent to root)
     */
    private formatPositionHierarchy(positionKey: string, orgParts: any[]): string {
        const chain = this.getPositionHierarchyChain(positionKey, orgParts);
        return chain.length > 0 ? chain.join(' → ') : '';
    }

    /**
     * Check if a node has children (for mat-tree)
     */
    public hasChild = (_: number, node: LineageTreeNode) => !!node.children && node.children.length > 0;

    /**
     * Check if a policy node has children (for mat-tree)
     */
    public hasPolicyChild = (_: number, node: PolicyLineageNode) => !!node.children && node.children.length > 0;

    /**
     * Check if a historical policy node has children (for mat-tree)
     */
    public hasHistoricalPolicyChild = (_: number, node: PolicyLineageNode) =>
        !!node.children && node.children.length > 0;

    /**
     * Build the Policy Lineage Tree showing:
     * Policy → Source (Hat) → Position details
     * Now includes ALL policies (pending, acknowledged, and info-only)
     */
    private buildPolicyLineageTree(policies: UserPolicyItem[]): void {
        const org = this.data.organization as any;
        const orgParts = org?.orgParts || [];
        const orgHats = org?.orgHats || {};

        const treeData: PolicyLineageNode[] = [];

        for (const item of policies) {
            // Determine status
            let status: 'acknowledged' | 'pending' | 'requires-reack' = 'pending';
            if (item.acknowledgment?.requiresReacknowledgment) {
                status = 'requires-reack';
            } else if (item.status === 'acknowledged') {
                status = 'acknowledged';
            } else if (item.status === 'not-required') {
                status = 'acknowledged'; // Show info-only as acknowledged in tree
            }

            // Create policy node
            const policyNode: PolicyLineageNode = {
                name: item.policyName || item.policyKey,
                type: 'policy',
                icon: 'policy',
                key: item.policyKey,
                status: status,
                children: [],
            };

            // Add source nodes (how policy was received - from hats)
            if (item.receivedViaHats && item.receivedViaHats.length > 0) {
                // For each hat that provides this policy
                for (const hatName of item.receivedViaHats) {
                    // Find the hat key from orgHats
                    const hatEntry = Object.entries(orgHats).find(([key, hat]: [string, any]) => hat?.name === hatName);
                    const hatKey = hatEntry ? hatEntry[0] : null;
                    const hatData = hatKey ? orgHats[hatKey] : null;

                    const sourceNode: PolicyLineageNode = {
                        name: hatName,
                        type: 'source-hat',
                        icon: 'school',
                        key: hatKey || undefined,
                        children: [],
                    };

                    // If we have hat data, show positions where this hat is
                    if (hatData) {
                        const positions = hatData?.positions || [];
                        const positionArray = hatData?.positionArray || [];
                        const allPositionKeys = new Set<string>();
                        const positionNames = new Map<string, string>();

                        // Positions array may contain objects with name property
                        for (const pos of positions) {
                            const posKey = pos?.key || pos?.partKey;
                            if (posKey) {
                                allPositionKeys.add(posKey);
                                if (pos?.name) {
                                    positionNames.set(posKey, pos.name);
                                }
                            }
                        }
                        for (const posKey of positionArray) {
                            if (posKey) allPositionKeys.add(posKey);
                        }

                        for (const posKey of allPositionKeys) {
                            let posName = positionNames.get(posKey);

                            if (!posName) {
                                const partData = orgParts.find((p: any) => p.key === posKey || p.partKey === posKey);
                                posName = partData?.name;
                            }

                            const posNode: PolicyLineageNode = {
                                name: posName || posKey,
                                type: 'position',
                                icon: 'work',
                                key: posKey,
                                hierarchy: this.formatPositionHierarchy(posKey, orgParts),
                            };
                            sourceNode.children!.push(posNode);
                        }
                    }

                    policyNode.children!.push(sourceNode);
                }
            } else {
                // No hat info - show as direct/unknown source
                policyNode.children!.push({
                    name: 'Direct Assignment',
                    type: 'source-direct',
                    icon: 'person',
                    children: [],
                });
            }

            // Add status node with completion details
            const statusDetails: string[] = [];
            if (item.acknowledgment?.hasRead) statusDetails.push('Read');
            if (item.acknowledgment?.hasUnderstood) statusDetails.push('Understood');
            if (item.acknowledgment?.hasAgreed) statusDetails.push('Agreed');

            // Determine display status
            const displayStatus = status;
            let statusName = 'Pending';
            let statusIcon = 'pending';

            if (item.status === 'not-required') {
                statusName = 'Info Only';
                statusIcon = 'info';
            } else if (status === 'acknowledged') {
                statusName = 'Acknowledged';
                statusIcon = 'check_circle';
            } else if (status === 'requires-reack') {
                statusName = 'Requires Re-Acknowledgment';
                statusIcon = 'refresh';
            }

            const statusNode: PolicyLineageNode = {
                name: statusName,
                type: 'status',
                icon: statusIcon,
                status: displayStatus,
                details: statusDetails.length > 0 ? statusDetails.join(' → ') : undefined,
            };
            policyNode.children!.push(statusNode);

            treeData.push(policyNode);
        }

        this.allPolicyLineageData = treeData;
        this.hasPolicyLineageData = treeData.length > 0;
        this.policyDataSource.data = treeData;

        // Expand all nodes
        if (treeData.length > 0) {
            this.policyTreeControl.dataNodes = treeData;
            this.expandAllPolicyNodes(treeData);
        }

        console.log('=== POLICY LINEAGE TREE ===');
        console.log('Tree data:', treeData);
    }

    /**
     * Recursively expand all policy tree nodes
     */
    private expandAllPolicyNodes(nodes: PolicyLineageNode[]): void {
        for (const node of nodes) {
            this.policyTreeControl.expand(node);
            if (node.children?.length) {
                this.expandAllPolicyNodes(node.children);
            }
        }
    }

    /**
     * Build policy lineage tree for historical policies (no longer assigned)
     */
    private buildHistoricalPolicyLineageTree(policies: UserPolicyItem[]): void {
        const treeData: PolicyLineageNode[] = [];

        for (const item of policies) {
            // Determine status
            let status: 'acknowledged' | 'pending' | 'requires-reack' = 'pending';
            if (item.acknowledgment?.requiresReacknowledgment) {
                status = 'requires-reack';
            } else if (item.status === 'acknowledged') {
                status = 'acknowledged';
            } else if (item.status === 'not-required') {
                status = 'acknowledged'; // Show info-only as acknowledged in tree
            }

            // Create policy node
            const policyNode: PolicyLineageNode = {
                name: item.policyName || item.policyKey,
                type: 'policy',
                icon: 'history',
                key: item.policyKey,
                status: status,
                children: [],
            };

            // Add completion date info
            const statusDetails: string[] = [];
            if (item.acknowledgment?.completedAt) {
                statusDetails.push('Completed: ' + this.formatDate(item.acknowledgment.completedAt));
            }
            if (item.acknowledgment?.policyVersion) {
                statusDetails.push('v' + item.acknowledgment.policyVersion);
            }

            // Determine display status
            let statusName = 'Pending';
            let statusIcon = 'pending';

            if (item.status === 'not-required') {
                statusName = 'Info Only';
                statusIcon = 'info';
            } else if (status === 'acknowledged') {
                statusName = 'Acknowledged';
                statusIcon = 'check_circle';
            } else if (status === 'requires-reack') {
                statusName = 'Requires Re-Acknowledgment';
                statusIcon = 'refresh';
            }

            const statusNode: PolicyLineageNode = {
                name: statusName,
                type: 'status',
                icon: statusIcon,
                status: status,
                details: statusDetails.length > 0 ? statusDetails.join(' | ') : undefined,
            };
            policyNode.children!.push(statusNode);

            // Add note about no longer assigned
            policyNode.children!.push({
                name: 'No longer assigned (removed from targeting hat)',
                type: 'source-direct',
                icon: 'info',
                children: [],
            });

            treeData.push(policyNode);
        }

        this.allHistoricalPolicyLineageData = treeData;
        this.hasHistoricalPolicyLineageData = treeData.length > 0;
        this.historicalPolicyDataSource.data = treeData;

        // Expand all nodes
        if (treeData.length > 0) {
            this.historicalPolicyTreeControl.dataNodes = treeData;
            this.expandAllHistoricalPolicyNodes(treeData);
        }
    }

    /**
     * Recursively expand all historical policy tree nodes
     */
    private expandAllHistoricalPolicyNodes(nodes: PolicyLineageNode[]): void {
        for (const node of nodes) {
            this.historicalPolicyTreeControl.expand(node);
            if (node.children?.length) {
                this.expandAllHistoricalPolicyNodes(node.children);
            }
        }
    }

    /**
     * Check if a position node has children (for mat-tree)
     */
    public hasPositionChild = (_: number, node: PositionLineageNode) => !!node.children && node.children.length > 0;

    /**
     * Build the Position Lineage Tree showing:
     * Position → Hats that place user at this position
     */
    private buildPositionLineageTree(): void {
        const hatsDetails = this.orgUserDetails?.hatsDetails || {};
        const org = this.data.organization as any;
        const orgHats = org?.orgHats || {};
        const orgParts = org?.orgParts || [];

        // Build a map of position -> hats that place user there
        const positionToHats = new Map<string, { hatKey: string; hatName: string; posName: string }[]>();

        for (const [hatKey, hatData] of Object.entries(hatsDetails)) {
            const hat = hatData as any;
            const fullHatData = orgHats[hatKey] || {};
            const hatName = hat?.name || fullHatData?.name || hatKey;

            // Get all positions for this hat
            const positions = hat?.positions || fullHatData?.positions || [];
            const positionArray = hat?.positionArray || fullHatData?.positionArray || [];
            const orgPosition = hat?.orgPosition || fullHatData?.orgPosition;

            // Process positions array (contains objects with key and name)
            for (const pos of positions) {
                const posKey = pos?.key || pos?.partKey;
                const posName = pos?.name || posKey;
                if (posKey) {
                    if (!positionToHats.has(posKey)) {
                        positionToHats.set(posKey, []);
                    }
                    positionToHats.get(posKey)!.push({ hatKey, hatName, posName });
                }
            }

            // Process positionArray (contains just keys)
            for (const posKey of positionArray) {
                if (posKey && !positionToHats.has(posKey)) {
                    const partData = orgParts.find((p: any) => p.key === posKey || p.partKey === posKey);
                    const posName = partData?.name || posKey;
                    positionToHats.set(posKey, []);
                    positionToHats.get(posKey)!.push({ hatKey, hatName, posName });
                } else if (posKey) {
                    // Add hat to existing position if not already there
                    const existing = positionToHats.get(posKey)!;
                    if (!existing.some((e) => e.hatKey === hatKey)) {
                        const partData = orgParts.find((p: any) => p.key === posKey || p.partKey === posKey);
                        const posName = partData?.name || posKey;
                        existing.push({ hatKey, hatName, posName });
                    }
                }
            }

            // Process orgPosition
            if (orgPosition?.key) {
                const posKey = orgPosition.key;
                const posName = orgPosition.name || posKey;
                if (!positionToHats.has(posKey)) {
                    positionToHats.set(posKey, []);
                }
                const existing = positionToHats.get(posKey)!;
                if (!existing.some((e) => e.hatKey === hatKey)) {
                    existing.push({ hatKey, hatName, posName });
                }
            }
        }

        // Build tree: Position → Hats
        const treeData: PositionLineageNode[] = [];

        for (const [posKey, hats] of positionToHats) {
            // Get position name from the hats data (first one that has it)
            const posName = hats[0]?.posName || posKey;

            const posNode: PositionLineageNode = {
                name: posName,
                type: 'position',
                icon: 'work',
                key: posKey,
                hierarchy: this.formatPositionHierarchy(posKey, orgParts),
                children: [],
            };

            // Add hats as children
            for (const hatInfo of hats) {
                posNode.children!.push({
                    name: hatInfo.hatName,
                    type: 'hat',
                    icon: 'school',
                    key: hatInfo.hatKey,
                });
            }

            treeData.push(posNode);
        }

        // Sort by position name
        treeData.sort((a, b) => a.name.localeCompare(b.name));

        this.allPositionLineageData = treeData;
        this.hasPositionLineageData = treeData.length > 0;
        this.positionDataSource.data = treeData;

        // Expand all nodes
        if (treeData.length > 0) {
            this.positionTreeControl.dataNodes = treeData;
            this.expandAllPositionNodes(treeData);
        }

        console.log('=== POSITION LINEAGE TREE ===');
        console.log('Tree data:', treeData);
    }

    /**
     * Recursively expand all position tree nodes
     */
    private expandAllPositionNodes(nodes: PositionLineageNode[]): void {
        for (const node of nodes) {
            this.positionTreeControl.expand(node);
            if (node.children?.length) {
                this.expandAllPositionNodes(node.children);
            }
        }
    }

    /**
     * Check if a security group node has children (for mat-tree)
     */
    public hasSecurityGroupChild = (_: number, node: SecurityGroupLineageNode) =>
        !!node.children && node.children.length > 0;

    /**
     * Build the Security Group Lineage Tree showing:
     * Security Group → Source (Hat/Direct) → Features → Permissions
     */
    private buildSecurityGroupLineageTree(): void {
        const hatsDetails = this.orgUserDetails?.hatsDetails || {};
        const org = this.data.organization as any;
        const orgHats = org?.orgHats || {};
        const orgSecurityGroups = org?.orgSecurityGroups || {};
        const orgParts = org?.orgParts || [];
        const userData = this.orgUserDetails as any;
        const directSecurityGroupsDetails = userData?.securityGroupsDetails || {};

        // Map: sgKey -> { sources: { type, key, name, positions? }[], sgData }
        const securityGroupSources = new Map<
            string,
            {
                sgData: any;
                sources: {
                    type: 'hat' | 'direct';
                    key?: string;
                    name: string;
                    positions?: { key: string; name: string }[];
                }[];
            }
        >();

        // Collect security groups from hats
        for (const [hatKey, hatData] of Object.entries(hatsDetails)) {
            const hat = hatData as any;
            const fullHatData = orgHats[hatKey] || {};
            const hatName = hat?.name || fullHatData?.name || hatKey;

            // Get security groups from this hat
            const securityGroupsArray = hat?.securityGroupsArray || fullHatData?.securityGroupsArray || [];
            const securityGroups = hat?.securityGroups || fullHatData?.securityGroups || [];

            const sgKeys = new Set<string>();
            for (const sg of [...securityGroupsArray, ...securityGroups]) {
                const sgKey = typeof sg === 'string' ? sg : sg?.key;
                if (sgKey) sgKeys.add(sgKey);
            }

            // Get positions for this hat
            const positions: { key: string; name: string }[] = [];
            const positionsArr = hat?.positions || fullHatData?.positions || [];
            const positionArray = hat?.positionArray || fullHatData?.positionArray || [];

            for (const pos of positionsArr) {
                const posKey = pos?.key || pos?.partKey;
                if (posKey) {
                    positions.push({
                        key: posKey,
                        name: pos?.name || posKey,
                    });
                }
            }

            for (const posKey of positionArray) {
                if (posKey && !positions.some((p) => p.key === posKey)) {
                    const partData = orgParts.find((p: any) => p.key === posKey || p.partKey === posKey);
                    positions.push({
                        key: posKey,
                        name: partData?.name || posKey,
                    });
                }
            }

            for (const sgKey of sgKeys) {
                const sgData = orgSecurityGroups[sgKey];
                if (!sgData) continue;

                if (!securityGroupSources.has(sgKey)) {
                    securityGroupSources.set(sgKey, { sgData, sources: [] });
                }

                securityGroupSources.get(sgKey)!.sources.push({
                    type: 'hat',
                    key: hatKey,
                    name: hatName,
                    positions: positions.length > 0 ? positions : undefined,
                });
            }
        }

        // Collect directly assigned security groups
        for (const [sgKey, sgDetails] of Object.entries(directSecurityGroupsDetails)) {
            const sgData = orgSecurityGroups[sgKey] || sgDetails;
            if (!sgData) continue;

            if (!securityGroupSources.has(sgKey)) {
                securityGroupSources.set(sgKey, { sgData, sources: [] });
            }

            // Check if not already added from a hat (avoid duplicate "Direct" entries)
            const existing = securityGroupSources.get(sgKey)!;
            if (!existing.sources.some((s) => s.type === 'direct')) {
                existing.sources.push({
                    type: 'direct',
                    name: 'Direct Assignment',
                });
            }
        }

        // Build tree: Security Group → Sources → Features → Permissions
        const treeData: SecurityGroupLineageNode[] = [];

        for (const [sgKey, { sgData, sources }] of securityGroupSources) {
            const sgNode: SecurityGroupLineageNode = {
                name: sgData?.name || sgKey,
                type: 'security-group',
                icon: 'security',
                key: sgKey,
                children: [],
            };

            // Add sources
            for (const source of sources) {
                const sourceNode: SecurityGroupLineageNode = {
                    name: source.name,
                    type: source.type === 'hat' ? 'source-hat' : 'source-direct',
                    icon: source.type === 'hat' ? 'school' : 'person',
                    key: source.key,
                    children: [],
                };

                // Add positions under hat source
                if (source.type === 'hat' && source.positions) {
                    for (const pos of source.positions) {
                        sourceNode.children!.push({
                            name: pos.name,
                            type: 'position',
                            icon: 'work',
                            key: pos.key,
                            hierarchy: this.formatPositionHierarchy(pos.key, orgParts),
                        });
                    }
                }

                sgNode.children!.push(sourceNode);
            }

            // Add features with their permissions
            const features = sgData?.features || [];
            const featureDetails = sgData?.featureDetails || {};

            for (const featureKey of features) {
                const featureNode: SecurityGroupLineageNode = {
                    name: featureKey,
                    type: 'feature',
                    icon: 'star',
                    key: featureKey,
                    children: [],
                };

                // Add permissions under this feature
                const permissions = featureDetails[featureKey]?.permissions || [];
                for (const perm of permissions) {
                    featureNode.children!.push({
                        name: perm,
                        type: 'permission',
                        icon: 'check_circle',
                        key: perm,
                    });
                }

                sgNode.children!.push(featureNode);
            }

            treeData.push(sgNode);
        }

        // Sort by security group name
        treeData.sort((a, b) => a.name.localeCompare(b.name));

        this.allSecurityGroupLineageData = treeData;
        this.hasSecurityGroupLineageData = treeData.length > 0;
        this.securityGroupDataSource.data = treeData;

        // Expand all nodes
        if (treeData.length > 0) {
            this.securityGroupTreeControl.dataNodes = treeData;
            this.expandAllSecurityGroupNodes(treeData);
        }

        console.log('=== SECURITY GROUP LINEAGE TREE ===');
        console.log('Tree data:', treeData);
    }

    /**
     * Recursively expand all security group tree nodes
     */
    private expandAllSecurityGroupNodes(nodes: SecurityGroupLineageNode[]): void {
        for (const node of nodes) {
            this.securityGroupTreeControl.expand(node);
            if (node.children?.length) {
                this.expandAllSecurityGroupNodes(node.children);
            }
        }
    }

    /**
     * Check if a menu lineage node has children (for mat-tree)
     */
    public hasMenuLineageChild = (_: number, node: MenuLineageNode) => !!node.children && node.children.length > 0;

    /**
     * Build the Menu Lineage Tree showing:
     * Menu Section → Menu Item → Feature → Source (Security Group/Direct) → Hat → Position
     */
    private buildMenuLineageTree(): void {
        const hatsDetails = this.orgUserDetails?.hatsDetails || {};
        const org = this.data.organization as any;
        const orgHats = org?.orgHats || {};
        const orgSecurityGroups = org?.orgSecurityGroups || {};
        const orgParts = org?.orgParts || [];
        const userData = this.orgUserDetails as any;
        const directFeatures = this.orgUserDetails?.features || [];

        // Build a map: feature -> sources (where the feature comes from)
        const featureSources = new Map<
            string,
            {
                fromDirect: boolean;
                fromSecurityGroups: {
                    sgKey: string;
                    sgName: string;
                    hatKey?: string;
                    hatName?: string;
                    positions?: { key: string; name: string }[];
                }[];
            }
        >();

        // Check direct user features
        for (const feature of directFeatures) {
            if (!featureSources.has(feature)) {
                featureSources.set(feature, { fromDirect: false, fromSecurityGroups: [] });
            }
            featureSources.get(feature)!.fromDirect = true;
        }

        // Check features from security groups via hats
        for (const [hatKey, hatData] of Object.entries(hatsDetails)) {
            const hat = hatData as any;
            const fullHatData = orgHats[hatKey] || {};
            const hatName = hat?.name || fullHatData?.name || hatKey;

            // Get positions for this hat
            const positions: { key: string; name: string }[] = [];
            const positionsArr = hat?.positions || fullHatData?.positions || [];
            const positionArray = hat?.positionArray || fullHatData?.positionArray || [];

            for (const pos of positionsArr) {
                const posKey = pos?.key || pos?.partKey;
                if (posKey) {
                    positions.push({ key: posKey, name: pos?.name || posKey });
                }
            }

            for (const posKey of positionArray) {
                if (posKey && !positions.some((p) => p.key === posKey)) {
                    const partData = orgParts.find((p: any) => p.key === posKey || p.partKey === posKey);
                    positions.push({ key: posKey, name: partData?.name || posKey });
                }
            }

            // Get security groups from this hat
            const securityGroupsArray = hat?.securityGroupsArray || fullHatData?.securityGroupsArray || [];
            const securityGroups = hat?.securityGroups || fullHatData?.securityGroups || [];

            const sgKeys = new Set<string>();
            for (const sg of [...securityGroupsArray, ...securityGroups]) {
                const sgKey = typeof sg === 'string' ? sg : sg?.key;
                if (sgKey) sgKeys.add(sgKey);
            }

            // For each security group, get its features
            for (const sgKey of sgKeys) {
                const sgData = orgSecurityGroups[sgKey];
                if (!sgData) continue;

                const sgName = sgData.name || sgKey;
                const features = sgData.features || [];

                for (const feature of features) {
                    if (!featureSources.has(feature)) {
                        featureSources.set(feature, { fromDirect: false, fromSecurityGroups: [] });
                    }

                    // Check if this SG is already recorded for this feature
                    const existing = featureSources.get(feature)!.fromSecurityGroups;
                    const existingEntry = existing.find((e) => e.sgKey === sgKey && e.hatKey === hatKey);

                    if (!existingEntry) {
                        existing.push({
                            sgKey,
                            sgName,
                            hatKey,
                            hatName,
                            positions: positions.length > 0 ? positions : undefined,
                        });
                    }
                }
            }
        }

        // Also check for directly assigned security groups (not via hat)
        const directSecurityGroupsDetails = userData?.securityGroupsDetails || {};
        for (const [sgKey, sgDetails] of Object.entries(directSecurityGroupsDetails)) {
            const sgData = orgSecurityGroups[sgKey] || (sgDetails as any);
            if (!sgData) continue;

            const sgName = sgData.name || sgKey;
            const features = sgData.features || [];

            for (const feature of features) {
                if (!featureSources.has(feature)) {
                    featureSources.set(feature, { fromDirect: false, fromSecurityGroups: [] });
                }

                // Check if this SG is already recorded without a hat
                const existing = featureSources.get(feature)!.fromSecurityGroups;
                const existingEntry = existing.find((e) => e.sgKey === sgKey && !e.hatKey);

                if (!existingEntry) {
                    existing.push({
                        sgKey,
                        sgName,
                        // No hatKey means direct SG assignment
                    });
                }
            }
        }

        // Handle system route inheritance: if ANY org feature is present, derive 'organization' feature
        // This ensures system route items (My Hats, My Policies, Org Board, Dashboard, Manage) appear in lineage
        if (!featureSources.has('organization')) {
            const orgSourceFeature = ORG_SYSTEM_FEATURES.find((f) => featureSources.has(f));
            if (orgSourceFeature) {
                const derivedSources = featureSources.get(orgSourceFeature)!;
                featureSources.set('organization', {
                    fromDirect: derivedSources.fromDirect,
                    fromSecurityGroups: [...derivedSources.fromSecurityGroups],
                });
            }
        }

        // Handle management tools system route: if ANY mgmt feature is present, derive 'management-tools-dashboard'
        if (!featureSources.has('management-tools-dashboard')) {
            const mgmtSourceFeature = MGMT_SYSTEM_FEATURES.find((f) => featureSources.has(f));
            if (mgmtSourceFeature) {
                const derivedSources = featureSources.get(mgmtSourceFeature)!;
                featureSources.set('management-tools-dashboard', {
                    fromDirect: derivedSources.fromDirect,
                    fromSecurityGroups: [...derivedSources.fromSecurityGroups],
                });
            }
        }

        // Build tree: Section → Menu Item → Feature → Sources
        const treeData: MenuLineageNode[] = [];
        const sectionMap = new Map<string, MenuLineageNode>();

        // Process each menu item
        for (const menuItem of MENU_ITEMS) {
            const sources = featureSources.get(menuItem.feature);
            if (!sources) continue; // User doesn't have this feature

            // Get or create section node
            if (!sectionMap.has(menuItem.section)) {
                sectionMap.set(menuItem.section, {
                    name: menuItem.section,
                    type: 'section',
                    icon: this.getSectionIcon(menuItem.section),
                    key: menuItem.section,
                    children: [],
                });
            }
            const sectionNode = sectionMap.get(menuItem.section)!;

            // Check if menu item already exists in section (avoid duplicates)
            let menuItemNode = sectionNode.children!.find((c) => c.name === menuItem.name);
            if (!menuItemNode) {
                menuItemNode = {
                    name: menuItem.name,
                    type: 'menu-item',
                    icon: menuItem.icon,
                    key: menuItem.name,
                    children: [],
                };
                sectionNode.children!.push(menuItemNode);
            }

            // Add feature node (might already exist if multiple menu items use same feature)
            let featureNode = menuItemNode.children!.find((c) => c.key === menuItem.feature);
            if (!featureNode) {
                featureNode = {
                    name: `Feature: ${menuItem.feature}`,
                    type: 'feature',
                    icon: 'star',
                    key: menuItem.feature,
                    children: [],
                };
                menuItemNode.children!.push(featureNode);
            }

            // Add direct source if applicable
            if (sources.fromDirect) {
                const existingDirect = featureNode.children!.find((c) => c.type === 'direct');
                if (!existingDirect) {
                    featureNode.children!.push({
                        name: 'Direct User Feature',
                        type: 'direct',
                        icon: 'person',
                        key: 'direct',
                    });
                }
            }

            // Add security group sources
            for (const sgSource of sources.fromSecurityGroups) {
                // Check if this SG already exists under feature
                let sgNode = featureNode.children!.find((c) => c.key === sgSource.sgKey);
                if (!sgNode) {
                    sgNode = {
                        name: sgSource.sgName,
                        type: 'security-group',
                        icon: 'security',
                        key: sgSource.sgKey,
                        children: [],
                    };
                    featureNode.children!.push(sgNode);
                }

                if (sgSource.hatKey) {
                    // Check if hat already exists under SG
                    let hatNode = sgNode.children!.find((c) => c.key === sgSource.hatKey);
                    if (!hatNode) {
                        hatNode = {
                            name: sgSource.hatName || sgSource.hatKey,
                            type: 'hat',
                            icon: 'school',
                            key: sgSource.hatKey,
                            children: [],
                        };
                        sgNode.children!.push(hatNode);
                    }

                    // Add positions under hat
                    if (sgSource.positions) {
                        for (const pos of sgSource.positions) {
                            const existingPos = hatNode.children!.find((c) => c.key === pos.key);
                            if (!existingPos) {
                                hatNode.children!.push({
                                    name: pos.name,
                                    type: 'position',
                                    icon: 'work',
                                    key: pos.key,
                                    hierarchy: this.formatPositionHierarchy(pos.key, orgParts),
                                });
                            }
                        }
                    }
                } else {
                    // Direct SG assignment (no hat)
                    const existingDirect = sgNode.children!.find((c) => c.type === 'direct');
                    if (!existingDirect) {
                        sgNode.children!.push({
                            name: 'Direct Assignment',
                            type: 'direct',
                            icon: 'person_add',
                            key: 'direct-sg',
                        });
                    }
                }
            }
        }

        // Convert section map to array
        const sectionOrder = ['Home', 'Workspace', 'Management Tools', 'Organization'];
        for (const section of sectionOrder) {
            const node = sectionMap.get(section);
            if (node && node.children && node.children.length > 0) {
                treeData.push(node);
            }
        }

        this.allMenuLineageData = treeData;
        this.hasMenuLineageData = treeData.length > 0;
        this.menuLineageDataSource.data = treeData;

        // Expand all nodes
        if (treeData.length > 0) {
            this.menuLineageTreeControl.dataNodes = treeData;
            this.expandAllMenuLineageNodes(treeData);
        }

        console.log('=== MENU LINEAGE TREE ===');
        console.log('Tree data:', treeData);
    }

    /**
     * Get icon for menu section
     */
    private getSectionIcon(section: string): string {
        switch (section) {
            case 'Home':
                return 'home';
            case 'Workspace':
                return 'work';
            case 'Management Tools':
                return 'engineering';
            case 'Organization':
                return 'corporate_fare';
            default:
                return 'folder';
        }
    }

    /**
     * Recursively expand all menu lineage tree nodes
     */
    private expandAllMenuLineageNodes(nodes: MenuLineageNode[]): void {
        for (const node of nodes) {
            this.menuLineageTreeControl.expand(node);
            if (node.children?.length) {
                this.expandAllMenuLineageNodes(node.children);
            }
        }
    }
}
